import { useState, useEffect, createContext, useContext } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Footprints,
  Package,
  User,
  Mail,
  LogOut,
  Search,
  FileText,
  Truck,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Eye,
  Phone,
  MapPin,
  Clock,
  Bell,
  MessageSquare,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  1: "bg-blue-100 text-blue-800",
  2: "bg-yellow-100 text-yellow-800",
  3: "bg-orange-100 text-orange-800",
  4: "bg-green-100 text-green-800",
  5: "bg-emerald-100 text-emerald-800",
};

// Create a context for notifications count
const NotificationContext = createContext({ count: 0, setCount: () => {} });

const Sidebar = () => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    fetchNotificationCount();
  }, [location.pathname]); // Refresh count when navigating

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/customer/message-notifications`);
      setNotificationCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch notification count");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const links = [
    { path: "/dashboard", icon: Package, label: "My Orders" },
    { path: "/dashboard/account", icon: User, label: "Account Details" },
    { path: "/dashboard/notifications", icon: Bell, label: "Notifications", badge: notificationCount },
    { path: "/dashboard/contact", icon: Mail, label: "Contact Us" },
  ];

  return (
    <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 hidden md:flex flex-col">
      {/* Red accent bar at top */}
      <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
      <div className="p-6 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2">
          <Footprints className="w-7 h-7 text-[#0F766E]" />
          <span className="text-lg font-bold text-[#0F766E] font-['Manrope']">
            Ortho Canada Lab
          </span>
        </Link>
      </div>
      <div className="p-4 border-b border-slate-200 bg-slate-50">
        <p className="text-xs text-slate-500 uppercase tracking-wider">Logged in as</p>
        <p className="font-medium text-slate-900 truncate mt-1">{user?.clinic_name}</p>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {links.map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className={`sidebar-link ${
              location.pathname === link.path ? "active" : ""
            }`}
            data-testid={`sidebar-${link.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <link.icon className="w-5 h-5" />
            <span className="flex-1">{link.label}</span>
            {link.badge > 0 && (
              <span className="bg-[#DC2626] text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                {link.badge}
              </span>
            )}
          </Link>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-200">
        <button
          onClick={handleLogout}
          className="sidebar-link w-full text-[#DC2626] hover:bg-red-50"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </aside>
  );
};

const MobileHeader = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    fetchNotificationCount();
  }, [location.pathname]);

  const fetchNotificationCount = async () => {
    try {
      const response = await axios.get(`${API}/customer/message-notifications`);
      setNotificationCount(response.data.length);
    } catch (error) {
      console.error("Failed to fetch notification count");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
      {/* Red accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#DC2626] to-[#0F766E]"></div>
      <div className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Footprints className="w-6 h-6 text-[#0F766E]" />
          <span className="font-bold text-[#0F766E]">OCL</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/dashboard">
            <Button variant={location.pathname === "/dashboard" ? "default" : "ghost"} size="sm" className={location.pathname === "/dashboard" ? "bg-[#0F766E]" : ""}>
              <Package className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/dashboard/account">
            <Button variant={location.pathname === "/dashboard/account" ? "default" : "ghost"} size="sm" className={location.pathname === "/dashboard/account" ? "bg-[#0F766E]" : ""}>
              <User className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/dashboard/notifications" className="relative">
            <Button variant={location.pathname === "/dashboard/notifications" ? "default" : "ghost"} size="sm" className={location.pathname === "/dashboard/notifications" ? "bg-[#0F766E]" : ""}>
              <Bell className="w-4 h-4" />
            </Button>
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {notificationCount}
              </span>
            )}
          </Link>
          <Link to="/dashboard/contact">
            <Button variant={location.pathname === "/dashboard/contact" ? "default" : "ghost"} size="sm" className={location.pathname === "/dashboard/contact" ? "bg-[#0F766E]" : ""}>
              <Mail className="w-4 h-4" />
            </Button>
          </Link>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 text-[#DC2626]" />
          </Button>
        </div>
      </div>
    </header>
  );
};

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("date_received");
  const [sortDirection, setSortDirection] = useState("desc");
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const filteredOrders = orders
    .filter((order) => {
      const term = searchTerm.toLowerCase();
      return (
        order.invoice_number.toLowerCase().includes(term) ||
        order.patient_name.toLowerCase().includes(term) ||
        order.status_label.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "date_received") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      if (sortField === "patient_name") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortDirection === "asc" ? (
      <ChevronUp className="w-4 h-4 ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">My Orders</h1>
          <p className="text-slate-600">Track your orthotic orders</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="orders-search-input"
          />
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No orders found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="order-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("invoice_number")}
                  >
                    <span className="flex items-center">
                      Invoice # <SortIcon field="invoice_number" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("patient_name")}
                  >
                    <span className="flex items-center">
                      Patient <SortIcon field="patient_name" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort("date_received")}
                  >
                    <span className="flex items-center">
                      Date Received <SortIcon field="date_received" />
                    </span>
                  </TableHead>
                  <TableHead>Item Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>
                    Documents <span className="text-xs font-normal text-slate-400">(Right click to open in new tab and download)</span>
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order, index) => (
                  <TableRow
                    key={order.id}
                    className={`animate-fade-in stagger-${Math.min(index + 1, 5)}`}
                  >
                    <TableCell>
                      <button
                        onClick={() => navigate(`/orders/${order.id}`)}
                        className="text-[#0F766E] font-medium hover:underline"
                        data-testid={`order-invoice-${order.id}`}
                      >
                        {order.invoice_number}
                      </button>
                    </TableCell>
                    <TableCell className="font-medium">{order.patient_name}</TableCell>
                    <TableCell>
                      {new Date(order.date_received).toLocaleDateString("en-CA")}
                    </TableCell>
                    <TableCell className="text-slate-600 max-w-[200px] truncate" title={order.item_description || "-"}>
                      {order.item_description || "-"}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[order.status]}>
                        {order.status_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ${order.cost.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {order.documents?.length > 0 ? (
                          <div className="flex gap-1">
                            {order.documents.map((doc) => {
                              const docTypeLabels = {
                                'invoice': 'Invoice',
                                'proof_of_manufacturing': 'Proof of Manufacturing',
                                'packing_slip': 'Packing Slip'
                              };
                              const docTypeLabel = docTypeLabels[doc.doc_type] || doc.doc_type;
                              return (
                                <a
                                  key={doc.id}
                                  href={`${API}/documents/${doc.id}/direct-download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                                  className="p-1 hover:bg-slate-100 rounded inline-flex items-center text-[#0F766E]"
                                  title={`${docTypeLabel}: ${doc.filename}`}
                                >
                                  <FileText className="w-4 h-4" />
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                        {order.status >= 4 && order.tracking_number && (
                          <button
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className="p-1 hover:bg-slate-100 rounded flex items-center gap-1 text-[#0F766E]"
                            title="View tracking"
                          >
                            <Truck className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {order.has_unread_messages && (
                          <button
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className="p-1 hover:bg-green-100 rounded flex items-center text-green-600 animate-pulse"
                            title="New message from admin"
                            data-testid={`order-unread-${order.id}`}
                          >
                            <Bell className="w-4 h-4" />
                          </button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/customer/message-notifications`);
      // Sort by reply_at descending (newest first)
      const sorted = response.data.sort((a, b) => {
        const dateA = new Date(a.reply_at || a.created_at);
        const dateB = new Date(b.reply_at || b.created_at);
        return dateB - dateA;
      });
      setNotifications(sorted);
    } catch (error) {
      toast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const dismissNotification = async (messageId) => {
    try {
      await axios.put(`${API}/customer/message-notifications/${messageId}/dismiss`);
      setNotifications(notifications.filter(n => n.id !== messageId));
      toast.success("Notification dismissed");
    } catch (error) {
      toast.error("Failed to dismiss notification");
    }
  };

  const dismissAll = async () => {
    try {
      for (const notif of notifications) {
        await axios.put(`${API}/customer/message-notifications/${notif.id}/dismiss`);
      }
      setNotifications([]);
      toast.success("All notifications dismissed");
    } catch (error) {
      toast.error("Failed to dismiss notifications");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Notifications</h1>
          <p className="text-slate-600">Admin replies to your messages</p>
        </div>
        {notifications.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={dismissAll}
            className="text-slate-600"
          >
            <Check className="w-4 h-4 mr-2" />
            Mark All as Read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No new notifications</p>
            <p className="text-sm text-slate-400 mt-2">
              When the admin replies to your messages, they will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notif) => (
            <Card key={notif.id} className="overflow-hidden border-l-4 border-l-[#0F766E]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-4 h-4 text-[#0F766E] flex-shrink-0" />
                      <span className="font-semibold text-slate-900">
                        Order: {notif.invoice_number}
                      </span>
                      {notif.patient_name && (
                        <span className="text-slate-500">
                          (Patient: {notif.patient_name})
                        </span>
                      )}
                    </div>
                    
                    <div className="bg-slate-50 rounded-lg p-3 mb-3">
                      <p className="text-xs text-slate-500 mb-1">Your message:</p>
                      <p className="text-sm text-slate-700">{notif.message}</p>
                    </div>
                    
                    <div className="bg-[#0F766E]/5 rounded-lg p-3 border border-[#0F766E]/20">
                      <p className="text-xs text-[#0F766E] mb-1 font-medium">Admin reply:</p>
                      <p className="text-sm text-slate-800">{notif.reply}</p>
                      <p className="text-xs text-slate-400 mt-2">{formatDate(notif.reply_at)}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-[#0F766E] hover:bg-[#115E59]"
                      onClick={() => navigate(`/orders/${notif.order_id}`)}
                    >
                      View Order
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => dismissNotification(notif.id)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const AccountDetails = () => {
  const { user, changePassword } = useAuth();
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Account Details</h1>
        <p className="text-slate-600">View and manage your account information</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Clinic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Contact Name</p>
                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Footprints className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Clinic Name</p>
                <p className="font-medium">{user?.clinic_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Address</p>
                <p className="font-medium">{user?.clinic_address}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-500">Phone</p>
                <p className="font-medium">{user?.phone}</p>
              </div>
            </div>
            {user?.username && (
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">Username</p>
                  <p className="font-medium">{user?.username}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, current_password: e.target.value })
                  }
                  required
                  data-testid="account-current-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, new_password: e.target.value })
                  }
                  required
                  data-testid="account-new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirm_password: e.target.value })
                  }
                  required
                  data-testid="account-confirm-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#0F766E] hover:bg-[#115E59]"
                disabled={loading}
                data-testid="account-change-password-btn"
              >
                {loading ? "Changing..." : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ContactUs = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Contact Us</h1>
        <p className="text-slate-600">Get in touch with Ortho Canada Lab</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#0F766E] mt-1" />
              <div>
                <p className="font-medium">Address</p>
                <p className="text-slate-600">503 Champagne Dr.</p>
                <p className="text-slate-600">North York ON M3J 2C6</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#0F766E] mt-1" />
              <div>
                <p className="font-medium">Phone</p>
                <p className="text-slate-600">(416) 250-9985</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 text-[#0F766E] mt-1" />
              <div>
                <p className="font-medium">Fax</p>
                <p className="text-slate-600">(647) 476-7183</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-[#0F766E] mt-1" />
              <div>
                <p className="font-medium">Email</p>
                <p className="text-slate-600">info@orthocanadalab.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#0F766E] mt-1" />
              <div>
                <p className="font-medium">Business Hours</p>
                <p className="text-slate-600">Monday - Friday: 9:00 AM - 4:00 PM</p>
                <p className="text-slate-600">Saturday - Sunday: Closed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#0F766E]/5 to-[#0F766E]/10 border-[#0F766E]/20">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#0F766E]" />
              Order-Specific Questions?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">
              For questions about a specific order, please use the messaging feature on the order details page. 
              Click on any invoice number in "My Orders" to access the order details and send a message directly to our team.
            </p>
            <div className="p-3 bg-white/50 rounded-lg border border-[#0F766E]/20">
              <p className="text-sm text-slate-500">
                <strong className="text-[#0F766E]">Tip:</strong> Messages sent through order details are linked to your specific order, 
                allowing our team to respond faster with the right context.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const CustomerDashboard = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <MobileHeader />
      <main className="md:ml-64 p-6 md:p-8" data-testid="customer-dashboard">
        <Routes>
          <Route index element={<MyOrders />} />
          <Route path="account" element={<AccountDetails />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="contact" element={<ContactUs />} />
        </Routes>
      </main>
    </div>
  );
};

export default CustomerDashboard;
